import React, { FC, useEffect, useRef, useState } from 'react'
import {observer} from 'mobx-react'
import _ from 'underscore'
import { t } from 'ttag'
import Vimeo from '@u-wave/react-vimeo'

import './DbsPlayer.css'
import { RefRange } from '../../scrRefs/RefRange'
import { getSLBiblePassages, SLBiblePassage } from '../../resources/albums'
import { Root } from '../../models3/Root'
import { ReferenceInput } from '../utils/ReferenceInput'
import { Dropdown, MenuItem } from 'react-bootstrap'
import { Project } from '../../models3/ProjectModels'
import useResizeObserver from "use-resize-observer"


// eslint-disable-next-line
import _debug from "debug"; let log = _debug('sltt:DbsPlayer') 

function defaultPassage(currentPassage: SLBiblePassage | undefined, passages: SLBiblePassage[]) {
    // If previously selected project and it's in new list, use first item from this previous project
    // If previously selected project not in list, use first item from American project
    // If previously selected project not in list and American not in list, select first item in list
    // If no previous project, use first from American
    // If no previous project and American not in list, select first item in list
    let americanPassages = passages.filter(p => p.languageName === 'American')
    let currentProjectPassages = passages.filter(p => p.languageName === currentPassage?.languageName)
    let nextPassage = currentProjectPassages[0] || americanPassages[0] || passages[0]
    return nextPassage
}

interface IDbsPlayer {
    rt: Root,
}

const DbsPlayer: FC<IDbsPlayer> = observer(({ rt }) => {
    const [dbsRefs, setDbsRefs] = useState<RefRange[]>([])
    const [passages, setPassages] = useState<SLBiblePassage[]>([])
    const [passage, setPassage] = useState<SLBiblePassage | undefined>(undefined)
    const [signedUrl, setSignedUrl] = useState('')

    let defaultReferenceId = 'DBSReference'

    const { ref, width = 640 } = useResizeObserver<HTMLDivElement>()

    // Whenever dbsRefs changes, fetch a list of associated passages and select a default passage
    useEffect(() => {
        let cancelled = false
        const currentRefs = dbsRefs;

        (async () => {
            let passages = await getSLBiblePassages(currentRefs)
            // Only update if dbsRefs hasn't changed since this call started
            // Since "Genesis" takes longer than "Genesis 1"
            // and "Genesis 1" takes longer than "Genesis 1:1"
            // it's possible that when a user types Genesis 1:1, they 
            // will get results for "Genesis" or "Genesis 1" since those
            // will take the longest to return
            if (!cancelled && _.isEqual(currentRefs, dbsRefs)) {
                setPassages(passages)
                setPassage(defaultPassage(passage, passages))
            }
        })()

        return () => { cancelled = true }
    }, [dbsRefs])

    // If we don't have any refs yet get them from localStorage default or rt.dbsRefs
    useEffect(() => {
        if (dbsRefs.length > 0) return

        // No references passed try to get a default reference from localStorage
        let defaultRefernce = rt.getDefault(defaultReferenceId) ?? ''
        log('defaultRerence', defaultRefernce)

        if (defaultRefernce) {
            let _refs = JSON.parse(defaultRefernce).map((r: any) => new RefRange(r.startRef, r.endRef))
            log('parseDefault', _refs)
            if (_refs.length) {
                setDbsRefs(_refs)
                return
            }      
        }
        
        // Set refs to current rt.dbsRefs
        if (rt.dbsRefs.length) {
            setDbsRefs(rt.dbsRefs)
        }
    }, [dbsRefs])

    function onClick(passage: SLBiblePassage) {
        log('onClick', JSON.stringify(passage, null, 4))
        setPassage(passage)
    }

    return (
        <div className="dbs-player-top-container" ref={ref}>
            <ResourcePlayerToolbar {...{ rt, setDbsRefs, defaultReferenceId, passages, passage, onClick }} />
            <div className='dbs-player-video-area'>
                { passage ? (
                    <DbsVideoMain passage={passage} width={width} /> 
                ) : (
                    <div>{t`No video selected.`}</div>
                )}
            </div>
        </div>
    )
})

type ResourcePlayerToolbarProps = {
    rt: Root,
    setDbsRefs: (refs: RefRange[]) => void,
    defaultReferenceId: string,
    passages: SLBiblePassage[],
    passage?: SLBiblePassage,
    onClick: (passage: SLBiblePassage) => void,
}

const ResourcePlayerToolbar: FC<ResourcePlayerToolbarProps> = observer(({ rt, setDbsRefs, defaultReferenceId, passages, passage, onClick }) => {
    const [errored, setErrored] = useState(false)
    let { dbsRefs } = rt // force re-render on change

    return (
        <div className='video-toolbar dbs-player-toolbar'>
            <div className='dbs-reference-search'>
                <ReferenceInput
                    refInput={rt}
                    refs={[]}
                    setRefs={setDbsRefs}
                    errored={errored}
                    setErrored={setErrored}
                    allowBookNameOnly={true}
                    includeGotoReferenceButton={true}
                    defaultReferenceId={defaultReferenceId} />
            </div>
            <div className="dbs-video-selector">
                { passage && 
                    <VideoSelector {...{ project: rt.project, passages, passage, onClick }} /> }
            </div>
        </div>
    )
})

function maybeCustomizeLanguageName(languageName: string) {
    // Customize the language name if needed
    if (languageName === 'Mexican Sign Language') {
        return 'LSM (Lengua de Señas Mexicana)'
    }
    return languageName
}

/**
 * Get the first letter of each upper-cased word in a string,
 * or all letters if a word is all upper-cased.
 * @param s The input string.
 * @returns A string containing the first letters of each word.
 */
function buildShortname(s: string) {
    if (s === 'Bible Society in Russia' || s === 'Russian Bible Society') {
        return 'BSR' // Special case for Bible Society in Russia
    }
    return s.split(' ')
        .filter((word: string) => !['BBB', 'CBT'].includes(word))
        .map((word: string) => {
            // Otherwise, return the first letter, followed by any unicode capital letters
            return word.charAt(0) + Array.from(word).filter((c, i) => i !== 0 && c === c.toUpperCase()).join('')
        }).join('')
}

function extractOrgAbbr(passage: SLBiblePassage) {
    const { teamName, copyright } = passage
    if (!teamName) {
        // use copyright as fallback, remove leading (c) and trim
        return copyright ? buildShortname(copyright.replace(/^\(c\)\s*/, '').trim()) : ''
    }

    if (teamName.includes('(')) {
        // if teamName already has an abbreviation in parentheses, use that
        const match = teamName.match(/\(([^)]+)\)/)
        if (match && match[1] === match[1].toUpperCase()) {
            // if the abbreviation is all upper-cased, return it
            return match[1]
        }
    }

    // otherwise, get the first letter of each word in the team name
    return buildShortname(teamName)
}

function displayedVideoName(passage: SLBiblePassage, project: Project) {
    let { name } = passage
    if (name.length > 80) {
        name = name.slice(0, 80) + '...'
    }
    if (name) { name = '(' + name + ')' }

    // get the first letter of each word in the team name unless it already has an abbr in parentheses, otherwise use the abbreviation in parentheses
    const orgAbbr = extractOrgAbbr(passage)
    return `${maybeCustomizeLanguageName(passage.languageName)} (${orgAbbr}) - ${RefRange.refRangesToDisplay(passage.refs, project)} ${name}`
}

type VideoSelectorProps = {
    project: Project,
    passages: SLBiblePassage[],
    passage: SLBiblePassage,
    onClick: (passage: SLBiblePassage) => void,
}

const VideoSelector: FC<VideoSelectorProps> = observer(({ project, passages, passage, onClick }) => {
    const sortedPassages = [...passages].sort((a, b) => {
        const ka = displayedVideoName(a, project)
        const kb = displayedVideoName(b, project)
        const cmp = ka.localeCompare(kb, undefined, { sensitivity: 'base', numeric: true })
        return cmp !== 0 ? cmp : a.url.localeCompare(b.url)
    })

    return (
        <Dropdown id='dbs-video-selector' pullRight>
            <Dropdown.Toggle className='sl-dropdown dbs-video-dropdown'>
                {displayedVideoName(passage, project)}
            </Dropdown.Toggle>
            <Dropdown.Menu className='scrollable-dropdown'>
                {sortedPassages.map(p => (
                    <MenuItem key={`${p.url}`} onSelect={() => onClick(p)} >
                        {displayedVideoName(p, project)}
                    </MenuItem>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    )
})


// <Vimeo videoId={ videoId } />,

interface IDbsVideoMain {
    passage: SLBiblePassage,
    width: number,
}

const DbsVideoMain: FC<IDbsVideoMain> = observer(({ passage, width }) => {
    let isVimeo = passage.url.includes('vimeo')

    let width2 = 20 * Math.round(width / 20)

    let hostedBy = ''
    if (passage.hostedBy !== 'United Bible Societies') {
        hostedBy = '/ ' + /* translator: important */ t`Hosted by: ` + passage.hostedBy
    }

    return (
        <div className="dbs-video-main">
            <div className='dbs-video-container'>
                {isVimeo ? 
                    <VimeoPlayer {...{passage, width}}/>
                    :
                    <video className="dbs-player-video" controls src={passage.url} />
                }
            </div>
            <p> {passage.copyright} {hostedBy} </p>
        </div>
    )
})

const VimeoPlayer: FC<IDbsVideoMain> = ({ passage, width }) => {
    function ss(u:string) { return u.split('/').slice(-1)[0] }

    let [url, setUrl] = useState(passage.url)
    log(`VimeoPlayer ${ss(passage.url)} / ${ss(url)}`)

    /**
     * There is a bug in <Vimeo> that causes it to fail to load private videos
     * when passage.url changes to a new video.
     * To work around this we remove the Vimeo control for 100ms
     * and then create a new control with the new url.
     */
    if (url && passage.url !== url) {
        setTimeout(() => { setUrl(passage.url) }, 100)
        return (<div></div>)
    }

    let width2 = 20 * Math.round(width / 20)

    return ( <Vimeo className="dbs-vimeo" 
                video={url} 
                showTitle={false} 
                showByline={false} 
                showPortrait={false}
                width={width2 - 50} 
                height={(width2 - 50) * 480 / 640} />
    )
}


export default DbsPlayer
